import * as React from "react";

/**
 * The countdown screen's loading state, shaped like the screen it precedes.
 *
 * Deliberately the same geometry as GameCountdown: two crest columns either
 * side of a divider, the date and time stack, four clock cells, the info strip.
 * A spinner in the middle of an empty page (what the v1 watch page showed here)
 * gives no hint of what is coming and makes the real content feel like it
 * appeared from nowhere; matching the layout means nothing jumps when it does.
 *
 * `v2-skeleton-wave` is the sheen, defined in src/v2/styles/v2.css. Sizes are
 * inline for the usual reason: Tailwind padding resolves to 0 inside `.v2`.
 */

function Bar({
  width,
  height = 12,
  radius = 6,
}: {
  width: number | string;
  height?: number;
  radius?: number;
}) {
  return (
    <div
      className="v2-skeleton-wave"
      style={{ width, height, borderRadius: radius }}
      aria-hidden="true"
    />
  );
}

function TeamColumnSkeleton() {
  return (
    <div
      className="flex flex-1 flex-col items-center"
      style={{ gap: "10px", minWidth: 0 }}
    >
      <div
        className="v2-skeleton-wave"
        style={{ width: 92, height: 92, borderRadius: 12 }}
        aria-hidden="true"
      />
      <Bar width={130} height={14} />
      <Bar width={64} height={10} />
    </div>
  );
}

export function GameCountdownSkeleton() {
  return (
    <div
      className="v2-countdown flex flex-col items-center"
      style={{ padding: "28px 16px 48px", gap: "18px", width: "100%" }}
      role="status"
      aria-label="Loading game"
    >
      <Bar width={200} height={22} />

      <div
        className="v2-countdown-matchup flex items-start justify-center"
        style={{ gap: "16px", width: "100%", maxWidth: "620px" }}
      >
        <TeamColumnSkeleton />
        <div
          className="flex items-center justify-center self-stretch"
          style={{
            borderLeft: "1px solid var(--v2-border)",
            borderRight: "1px solid var(--v2-border)",
            padding: "0 26px",
            minHeight: "92px",
          }}
        >
          <Bar width={20} height={12} />
        </div>
        <TeamColumnSkeleton />
      </div>

      <div className="flex flex-col items-center" style={{ gap: "8px" }}>
        <Bar width={110} height={11} />
        <Bar width={240} height={34} radius={8} />
      </div>

      <div
        className="v2-countdown-clock flex flex-wrap items-center justify-center"
        style={{ gap: "14px" }}
      >
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className="v2-skeleton-wave"
            style={{ width: 84, height: 76, borderRadius: 8 }}
            aria-hidden="true"
          />
        ))}
      </div>

      <Bar width={280} height={12} />

      <div
        className="v2-countdown-info flex flex-wrap items-start justify-center"
        style={{
          borderTop: "1px solid var(--v2-border)",
          paddingTop: "20px",
          marginTop: "6px",
          width: "100%",
          maxWidth: "760px",
        }}
      >
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="v2-countdown-info-item flex flex-1 justify-center"
            style={{
              padding: "0 22px",
              borderLeft: i === 0 ? undefined : "1px solid var(--v2-border)",
              minWidth: "200px",
            }}
          >
            <div className="flex items-start" style={{ gap: "10px" }}>
              <div
                className="v2-skeleton-wave shrink-0"
                style={{ width: 20, height: 20, borderRadius: 10 }}
                aria-hidden="true"
              />
              <div className="flex flex-col" style={{ gap: "6px" }}>
                <Bar width={110} height={12} />
                <Bar width={80} height={10} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default GameCountdownSkeleton;
